import React from 'react';
import {
  Button,
  Typography as MuiTypography,
  Grid,
  Box,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { useHistory } from 'react-router-dom';
import { useAnalyticsPageView } from '../../common/hooks/useAnalyticsPageView';
import { useAdobeActions } from '../../common/hooks/useAnalyticsUIEvents';
import { useTrackingNumberRedirect } from '../../common/hooks/useTrackingNumberRedirect';
import { Typography } from '@chr/eds-react';
import { useChrLdFlag } from '@chr/common-launchdarkly';
import {
  REFERENCE_NUMBER_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  isValidInput,
  isValidPostalCodeInput,
  getInputErrorMessage,
  getPostalCodeErrorMessage
} from '../../common/utilities/inputValidation';

// --- Style helper functions ---

const getContainerHeight = (enhancedSearch: boolean, activeTab: string): string => {
  if (!enhancedSearch) return '500px';
  return activeTab === 'tracking' ? '525px' : '625px';
};

const getMinHeight = (enhancedSearch: boolean, activeTab: string): string => {
  if (!enhancedSearch) return '870px';
  return activeTab === 'tracking' ? '875px' : '975px';
};

const getContainerPadding = (isMobileScreen: boolean, enhancedSearch: boolean): string => {
  if (isMobileScreen) return '40px 24px';
  return enhancedSearch ? '65px 48px' : '70px 50px';
};

const getSubtitleText = (
  enhancedSearch: boolean,
  isMobileScreen: boolean,
  translate: (key: string) => string
): string => {
  if (!enhancedSearch) {
    return translate('Enter your order or shipment Tracking Number to get instant freight tracking information.');
  }
  if (isMobileScreen) {
    return translate('Enter your Order or Shipment Tracking Number or Reference Number.');
  }
  return translate('Enter your order or shipment Tracking Number, or other Reference Number to get instant freight tracking information.');
};

const isTrackDisabled = (
  enhancedSearch: boolean,
  activeTab: string,
  chrTrackingNumber: string,
  referenceNumber: string,
  postalCode: string
): boolean => {
  if (!enhancedSearch) return !chrTrackingNumber;
  if (activeTab === 'tracking') return !chrTrackingNumber;
  return !isValidInput(referenceNumber) || !isValidPostalCodeInput(postalCode);
};

const getTrackButtonSx = (enhancedSearch: boolean) => ({
  width: '100%',
  ...(!enhancedSearch && { minWidth: '248px' }),
  maxWidth: '422px',
  alignItems: 'center',
});

const getTrackGridSx = (enhancedSearch: boolean) => ({
  paddingTop: 3,
  ...(enhancedSearch && { display: 'flex', justifyContent: 'center' }),
});

const getToggleButtonSx = (
  isActive: boolean,
  side: 'left' | 'right'
) => {
  const borderRadius = side === 'left' ? '4px 0 0 4px' : '0 4px 4px 0';

  const baseSx = {
    flex: 1,
    borderRadius,
    minWidth: '100px',
    height: '46px',
    textTransform: 'none' as const,
    fontSize: '14px',
    fontWeight: 600,
  };

  if (isActive) {
    return {
      ...baseSx,
      color: 'primary.main',      
      border: '2px solid',
      backgroundColor: 'rgba(0, 113, 199, 0.08)',
      borderColor: 'primary.main',
      '&.MuiToggleButtonGroup-grouped:not(:last-of-type)': {
        borderRight: '2px solid',
        borderRightColor: 'primary.main',
      },
      '&.Mui-selected': {
        color: 'primary.main',
        borderColor: 'primary.main',
        backgroundColor: 'rgba(0, 113, 199, 0.08)',     
      },
      '&.Mui-selected:hover': {
        backgroundColor: 'rgba(0, 113, 199, 0.08)',     
      },
    };
  }

  return {
    ...baseSx,
    color: 'primary.main',
    backgroundColor: 'white',
    border: '1px solid #E0E0E0',    
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
    '&:active': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
    }
  };
};

// --- Sub-components ---

interface HomeHeaderProps {
  enhancedSearch: boolean;
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const HomeHeader: React.FC<HomeHeaderProps> = ({ enhancedSearch, isMobileScreen, translate }) => {
  const title = translate('Tracking starts here');
  const subtitle = getSubtitleText(enhancedSearch, isMobileScreen, translate);

  return (
    <Stack direction="column" spacing={2} sx={{ marginBottom: 3 }}>
      <MuiTypography variant="h3" align="center" sx={{
        fontFamily: 'Nunito Sans',
        fontSize: '32px',
        fontWeight: 700,
        lineHeight: '42px',
        ParagrahhSpacking: '16px',
      }}>
        {title}
      </MuiTypography>
      <MuiTypography variant="body1" align="center">{subtitle}</MuiTypography>
    </Stack>
  );
};

interface TrackingPanelProps {
  chrTrackingNumber: string;
  setChrTrackingNumber: (value: string) => void;
  onTrack: () => void;
  translate: (key: string) => string;
}

const TrackingPanel: React.FC<TrackingPanelProps> = ({
  chrTrackingNumber, setChrTrackingNumber, onTrack, translate,
}) => (
  <Stack direction="column" spacing={0.5} sx={{ width: '100%', maxWidth: '422px', margin: '0 auto' }}>
    <Typography variant="subtitle300">{translate('Tracking Number *')}</Typography>
    <TextField
      data-testid="home-chr-tracking-number"
      onChange={(e) => setChrTrackingNumber(e.target.value)}
      onKeyUp={(e) => {
        if (chrTrackingNumber && e.key === 'Enter') {
          onTrack();
        }
      }}
      fullWidth
    />
    <Typography variant="caption" color="text.secondary">
      {translate('This field is alphanumeric. Eg. ABC123DE4F5GH67')}
    </Typography>
  </Stack>
);

interface ReferencePanelProps {
  referenceNumber: string;
  setReferenceNumber: (value: string) => void;
  postalCode: string;
  setPostalCode: (value: string) => void;
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const ReferencePanel: React.FC<ReferencePanelProps> = ({
  referenceNumber, setReferenceNumber, postalCode, setPostalCode, isMobileScreen, translate,
}) => {
  const referenceHint = isMobileScreen
    ? translate('Enter a PO or BOL number.')
    : translate('Enter a BOL number, PRO Number, CHR Order or Shipment Number');

  const referenceError = getInputErrorMessage(referenceNumber, translate);
  const postalCodeError = getPostalCodeErrorMessage(postalCode, translate);

  return (
    <Stack direction="column" spacing={2} sx={{ width: '100%', maxWidth: '422px', margin: '0 auto' }}>
      <Stack direction="column" spacing={0.5}>
        <Typography variant="subtitle300">{translate('Reference Number *')}</Typography>
        <TextField
          data-testid="home-reference-number"
          value={referenceNumber}
          onChange={(e) => setReferenceNumber(e.target.value)}
          error={!!referenceError}
          helperText={referenceError}
          inputProps={{ maxLength: REFERENCE_NUMBER_MAX_LENGTH }}
          fullWidth
        />
        {!referenceError && (
          <Typography variant="caption" color="text.secondary">{referenceHint}</Typography>
        )}
      </Stack>
      <Stack direction="column" spacing={0.5}>
        <Typography variant="subtitle300">{translate('Origin or Destination Postal Code *')}</Typography>
        <TextField
          data-testid="home-postal-code"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          error={!!postalCodeError}
          helperText={postalCodeError}
          inputProps={{ maxLength: POSTAL_CODE_MAX_LENGTH }}
          fullWidth
        />
      </Stack>
    </Stack>
  );
};

interface EnhancedSearchFormProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  chrTrackingNumber: string;
  setChrTrackingNumber: (value: string) => void;
  referenceNumber: string;
  setReferenceNumber: (value: string) => void;
  postalCode: string;
  setPostalCode: (value: string) => void;
  onTrack: () => void;
  isMobileScreen: boolean;
  translate: (key: string) => string;
}

const EnhancedSearchForm: React.FC<EnhancedSearchFormProps> = ({
  activeTab, setActiveTab, chrTrackingNumber, setChrTrackingNumber,
  referenceNumber, setReferenceNumber, postalCode, setPostalCode,
  onTrack, isMobileScreen, translate,
}) => (
  <>
    <Grid item xs={12} sx={{ paddingBottom: '4px', display: 'flex', justifyContent: 'center', width: '100% !important' }}>
      <ToggleButtonGroup
        value={activeTab}
        exclusive
        onChange={(_, value) => { if (value) setActiveTab(value); }}
        sx={{
          minWidth: '140px',
          width: '100%',
          maxWidth: '422px',
          '& .MuiToggleButtonGroup-grouped': {
            border: '1px solid #E0E0E0',
          },
        }}
      >
        <ToggleButton
          value="tracking"
          sx={getToggleButtonSx(activeTab === 'tracking', 'left')}
        >
          {translate('Tracking No.')}
        </ToggleButton>
        <ToggleButton
          value="reference"
          sx={getToggleButtonSx(activeTab === 'reference', 'right')}
        >
          {translate('Reference No.')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Grid>
    <Grid item xs={12} sx={{ paddingTop: 2 }}>
      {activeTab === 'tracking' && (
        <TrackingPanel
          chrTrackingNumber={chrTrackingNumber}
          setChrTrackingNumber={setChrTrackingNumber}
          onTrack={onTrack}
          translate={translate}
        />
      )}
      {activeTab === 'reference' && (
        <ReferencePanel
          referenceNumber={referenceNumber}
          setReferenceNumber={setReferenceNumber}
          postalCode={postalCode}
          setPostalCode={setPostalCode}
          isMobileScreen={isMobileScreen}
          translate={translate}
        />
      )}
    </Grid>
  </>
);

interface LegacySearchFormProps {
  chrTrackingNumber: string;
  setChrTrackingNumber: (value: string) => void;
  onTrack: () => void;
  translate: (key: string) => string;
}

const LegacySearchForm: React.FC<LegacySearchFormProps> = ({
  chrTrackingNumber, setChrTrackingNumber, onTrack, translate,
}) => (
  <>
    <Grid item xs={12} sx={{ paddingBottom: '4px' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%', minWidth: '248px', maxWidth: '422px' }}
      >
        <MuiTypography variant="subtitle2">{translate('Tracking Number *')}</MuiTypography>
      </Stack>
    </Grid>
    <Grid item xs={12}>
      <TextField
        data-testid="home-chr-tracking-number"
        onChange={(e) => setChrTrackingNumber(e.target.value)}
        onKeyUp={(e) => {
          if (chrTrackingNumber && e.key === 'Enter') {
            onTrack();
          }
        }}
        InputProps={{
          style: { width: '100%', minWidth: '248px', maxWidth: '422px' },
        }}
        fullWidth
      />
    </Grid>
    <Grid item xs={12} sx={{ paddingTop: 1 }}>
      <MuiTypography variant="caption" color="text.secondary">
        {translate('This field is alphanumeric. Eg. ABC123DE4F5GH67')}
      </MuiTypography>
    </Grid>
  </>
);

export const Home = () => {
  const translate = useTranslation();
  const { breakpoints } = useTheme();
  const isMobileScreen = useMediaQuery(breakpoints.down('md'));
  const isXs = useMediaQuery(breakpoints.down('sm'));
  const [chrTrackingNumber, setChrTrackingNumber] = React.useState<string>('');
  const [activeTab, setActiveTab] = React.useState<string>('tracking');
  const [referenceNumber, setReferenceNumber] = React.useState<string>('');
  const [postalCode, setPostalCode] = React.useState<string>('');
  const history = useHistory();
  const { handleAdobeAction } = useAdobeActions();
  const enhancedSearch = useChrLdFlag('enhanced-externaltracking-search', false);
  useAnalyticsPageView({ "pageName": "Search Page", "pagePath": "/", "siteName": "Online.ExternalTracking.UI" });
  useTrackingNumberRedirect();



  const onTrack = () => {
    if (enhancedSearch && activeTab === 'reference') {
      setChrTrackingNumber('');
      handleAdobeAction('button', 'click', 'Track Reference(Home)');
      history.push(`/detail/${encodeURIComponent(referenceNumber)}?postalCode=${encodeURIComponent(postalCode)}`);
      return;
    }
    setReferenceNumber('');
    setPostalCode('');
    handleAdobeAction('button', 'click', 'Track Shipment(Home)');
    history.push(`/detail/${encodeURIComponent(chrTrackingNumber)}`);
  };

  return (
    <Grid
      data-testid="home-main-grid"
      container
      sx={{
        paddingTop: '30px',
        flex: 1,
        ...(isXs ? { minHeight: getMinHeight(enhancedSearch, activeTab) } : {}),
      }}
    >
      <Box
        sx={{
          width: isMobileScreen ? '90%' : '50%',
          margin: '0 auto',
          backgroundColor: 'white',
          padding: getContainerPadding(isMobileScreen, enhancedSearch),
          borderRadius: '4px',
          minHeight: getContainerHeight(enhancedSearch, activeTab),
          alignItems: 'center',
        }}
      >
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{
            width: isMobileScreen ? '100%' : '70%',
            margin: 'auto',
            ...(!isMobileScreen && { minWidth: enhancedSearch ? '300px' : '248px' }),
            maxWidth: enhancedSearch ? '520px' : '422px',
          }}
        >
          <Grid item xs={12}>
            <HomeHeader
              enhancedSearch={enhancedSearch}
              isMobileScreen={isMobileScreen}
              translate={translate}
            />
          </Grid>
          {enhancedSearch ? (
            <EnhancedSearchForm
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              chrTrackingNumber={chrTrackingNumber}
              setChrTrackingNumber={setChrTrackingNumber}
              referenceNumber={referenceNumber}
              setReferenceNumber={setReferenceNumber}
              postalCode={postalCode}
              setPostalCode={setPostalCode}
              onTrack={onTrack}
              isMobileScreen={isMobileScreen}
              translate={translate}
            />
          ) : (
            <LegacySearchForm
              chrTrackingNumber={chrTrackingNumber}
              setChrTrackingNumber={setChrTrackingNumber}
              onTrack={onTrack}
              translate={translate}
            />
          )}
          {enhancedSearch && (
            <Grid item xs={12} sx={{ paddingTop: 2, display: 'flex', justifyContent: 'center' }}>
              <MuiTypography variant="subtitle300" sx={{ fontStyle: 'italic', width: '100%', maxWidth: '422px' }}>
                {translate('* These fields are required')}
              </MuiTypography>
            </Grid>
          )}
          <Grid item xs={12} sx={getTrackGridSx(enhancedSearch)}>
            <Button
              id="track-btn"
              variant="contained"
              onClick={onTrack}
              disabled={isTrackDisabled(enhancedSearch, activeTab, chrTrackingNumber, referenceNumber, postalCode)}
              sx={getTrackButtonSx(enhancedSearch)}
            >
              {translate('Track')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default Home;
